import React, { FC } from "react";

import {
  ActionValue,
  basketElementsSelector,
  DataViewKey,
  DOWNLOAD_MANAGER_FORMAT_BY,
  downloadManagerPreferenceFormatByState,
  downloadManagerSelectionSelector,
  KpkMedia,
  MEDIA_TYPE_RICH_ELEMENT,
  SentryCallback,
  useBasketRemove,
  useCurrentUser,
  useShareToken,
} from "@keepeek/commons";
import {
  ActionDisplayType,
  Actions,
  BasketElement,
  withSuspense,
} from "@keepeek/refront-components";
import { Typography } from "@mui/material";
import { useRouter } from "next/router";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { KIcon } from "../../../components/common/KIcon";
import { CONFIGURATION_SECTION_PATH } from "../../../components/layout/adminMenu";
import logger from "../../../lib/logger-utils";
import { Basket } from "../../../models/configuration/components/basket";
import { Dataview } from "../../../models/configuration/components/dataview";
import { Search } from "../../../models/configuration/pages/search";
import { useBackContext } from "../../../providers/backContext/hooks";
import { currentBasketIdState } from "../../../providers/basket/atoms";
import { useConfiguration } from "../../../providers/config/hooks";

type BasketElementsProps = {
  sentryCallback?: SentryCallback;
};

const BasketElements: FC<BasketElementsProps> = function ({ sentryCallback }) {
  const router = useRouter();
  const { shareTokenDownloadAuthorized } = useShareToken(router.pathname, router.query.tokenId);
  const dataViewConfig = useConfiguration<Dataview>(CONFIGURATION_SECTION_PATH.COMPONENTS_DATAVIEW);
  const { hasContactSheets } = useCurrentUser();
  const contactSheetsEnabled = (dataViewConfig?.contactSheets && hasContactSheets) || false;
  const pageSearchConfig = useConfiguration<Search>(CONFIGURATION_SECTION_PATH.PAGES_SEARCH);
  const displayShowAttachmentButton = pageSearchConfig?.authorizedActions?.attachments || false;
  const currentBasketId = useRecoilValue(currentBasketIdState);
  const { remove } = useBasketRemove(currentBasketId, sentryCallback);
  const { goToPageWithBackContext } = useBackContext();

  const setSelectionToDownloadManager = useSetRecoilState(
    downloadManagerSelectionSelector(DataViewKey.BASKET),
  );

  const currentBasketElements = useRecoilValue(basketElementsSelector(currentBasketId));
  const basketConfig = useConfiguration<Basket>(CONFIGURATION_SECTION_PATH.COMPONENTS_BASKET);
  const setPreferenceFormat = useSetRecoilState(downloadManagerPreferenceFormatByState);
  const handleActionClick = (action: ActionValue, elementId: KpkMedia["id"]) => {
    switch (action) {
      case ActionValue.DOWNLOAD:
        setSelectionToDownloadManager([elementId]);
        break;
      case ActionValue.REMOVE_FROM_BASKET:
        remove(elementId);
        break;
      case ActionValue.OPEN_ELEMENT:
        router.push(`/element?id=${elementId}`);
        break;
      case ActionValue.SHOW_ATTACHMENT:
        setPreferenceFormat(DOWNLOAD_MANAGER_FORMAT_BY.OTHER);
        setSelectionToDownloadManager([elementId]);
        break;
      default:
        logger.warn("BasketElements - handleActionClick, action not yet handled:", action);
        break;
    }
  };
  if (!currentBasketElements?.elements || currentBasketElements.elements.length === 0) {
    return null;
  }
  return (
    <>
      {currentBasketElements.elements &&
        currentBasketElements.elements.map((element) => {
          const enabledActions = [
            ActionValue.DOWNLOAD,
            ActionValue.CONTACT_SHEETS,
            ActionValue.REMOVE_FROM_BASKET,
          ];
          if (
            displayShowAttachmentButton &&
            !!element.attachmentCounts &&
            !!element.attachmentCounts.online &&
            element.attachmentCounts.online > 0
          ) {
            enabledActions.push(ActionValue.SHOW_ATTACHMENT);
          }

          return (
            <BasketElement
              key={element.id}
              element={element}
              sxCard={{ backgroundSize: basketConfig?.elementImageSize ? "contain" : "cover" }}
              onNavigate={() => {
                handleActionClick(ActionValue.OPEN_ELEMENT, element.id);
                goToPageWithBackContext({
                  sourcePage: { pathname: "/search" },
                  targetPage: { pathname: `/element`, query: { id: element.id } },
                  key: DataViewKey.BASKET,
                });
              }}
              actions={
                <Actions
                  isRichElement={element.mediaType === MEDIA_TYPE_RICH_ELEMENT}
                  isBasket={true}
                  isSelection={false}
                  displayType={ActionDisplayType.ACTION_BUTTON}
                  onActionClick={(action) => {
                    handleActionClick(action, element.id);
                  }}
                  // One selected element for labels
                  selectedElements={[element.id]}
                  KIcon={KIcon}
                  shareTokenDownloadAuthorized={shareTokenDownloadAuthorized}
                  contactSheetsEnabled={contactSheetsEnabled}
                  enabledActions={enabledActions}
                />
              }
            >
              <Typography variant="body2">{element.title.value}</Typography>
            </BasketElement>
          );
        })}
    </>
  );
};

export default withSuspense(BasketElements);
